/**
 * PromoterDashboardRewardsByRank.tsx - Rank-Based Rewards Display
 *
 * This component displays rank-based rewards with the promoter's current
 * ranking and progress toward achieving competitive reward tiers.
 *
 * Key Features:
 * - Rank-based reward tiers with position requirements
 * - Current promoter ranking and position tracking
 * - Minimum donation requirements for rank eligibility
 * - Competitive elements and peer comparison
 * - Achievement status and reward eligibility
 * - Cascading logic explanation and visualization
 *
 * Rank Reward Display:
 * - Rank ranges (e.g., "1st", "2nd-3rd", "4th-10th")
 * - Minimum donation requirements for each rank
 * - Current promoter position and eligibility
 * - Reward descriptions and benefits
 * - Achievement status indicators
 *
 * Competitive Elements:
 * - Current ranking position with visual indicators
 * - Distance to next rank improvement
 * - Peer performance comparison
 * - Motivation messaging for rank advancement
 * - Achievement celebration for rank rewards
 *
 * Cascading Logic:
 * - Explanation of how ranks are assigned
 * - Impact of minimum donation requirements
 * - How promoters are bumped between tiers
 * - Fair distribution of competitive rewards
 *
 * Props:
 * - promoterId: string - ID of promoter for rank tracking
 * - currentRank: number - Current ranking position
 * - currentAmount: number - Current amount raised
 *
 * Dependencies: MobX store, ranking utilities, cascading calculations
 */

import React, { useState } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import {
  ArrowUp,
  ArrowDown,
  XCircle,
  ArrowUpRight,
  Circle,
  ArrowDownRight,
} from "lucide-react";
import { fundraiserStore } from "../../stores/FundraiserStore";
import {
  calculatePromoterRankLevel,
  getPromotersForRankLevel,
  assignPromotersToRankTiers,
} from "../../utils/rankUtils";
import { PromoterProgressModel, PromoterWithRank } from "../../models";
import { RankAchievementModel } from "../../models/FundraisersGridModels";
import Up from "../../../../../images/fundraiser/Up.svg";
import Down from "../../../../../images/fundraiser/Down.svg";
import Current from "../../../../../images/fundraiser/current.svg";
import None from "../../../../../images/fundraiser/none.svg";

// New components for By Rank table design
const RankTable = styled.div`
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f5f9;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const RankTableHeader = styled.div`
  display: grid;
  grid-template-columns: 100px 3fr 2fr 3fr;
  gap: 24px; /* Adjusted gap for better alignment */
  padding: 12px 32px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 14px;
  font-weight: 700;
  color: #6e7882;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const RankTableRow = styled.div`
  display: grid;
  grid-template-columns: 100px 3fr 2fr 3fr;
  gap: 24px;
  padding: 4px 32px;
  border-bottom: 1px solid #f1f5f9;
  align-items: center;
  transition: all 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f8fafc;
  }
`;

const RankValue = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: #1a1e24;
  text-align: left;
`;

const RewardValue = styled.div`
  font-size: 16px;
  font-weight: normal;
  color: #1a1e24;
  text-align: left;
  white-space: nowrap;
  margin-left: -45px;
`;

const PromotersColumn = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: flex-start;
  margin-left: -40px;
`;

const PromoterCountBadge = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f1f5f9;
  border: 2px solid #cbd5e1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 800;
  color: #64748b;
  flex-shrink: 0;
`;

const PromoterCircle = styled.div<{ isCurrentPromoter: boolean }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 3px solid
    ${(props) => (props.isCurrentPromoter ? "#3b82f6" : "#e2e8f0")};
  overflow: hidden;
  position: relative;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const PromoterCircleImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

const PromoterCircleInitials = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  background: #f8f8f8;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ResultsColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  min-height: 70px;
  justify-content: center;
  text-align: left;
  margin-left: 50px;
`;

const ResultsText = styled.div<{
  variant: "gain" | "lose" | "current" | "none";
}>`
  font-size: 16px;
  font-weight: 400;
  color: ${(props) => {
    if (props.variant === "gain") return "#12B981";
    if (props.variant === "lose") return "#DE5F85";
    if (props.variant === "none") return "#808A97";
    return "#3b82f6";
  }};
  display: flex;
  align-items: center;
  gap: 6px;

  .bold {
    font-weight: 700;
  }
`;

const CurrentPromoterText = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: #518ef8;
  display: flex;
  align-items: center;
  gap: 6px;

  .bold {
    font-weight: 400;
  }
`;

const NoRewardRow = styled.div`
  display: grid;
  grid-template-columns: 100px 3fr 2fr 3fr;
  gap: 24px;
  padding: 4px 32px;
  border-bottom: 1px solid #f1f5f9;
  align-items: center;
  transition: all 0.2s ease;
  background: #f8fafc;

  &:hover {
    background: #f1f5f9;
  }
`;

const RankValueCurrent = styled.div<{ isCurrent: boolean }>`
  font-size: 16px;
  font-weight: 400;
  color: ${(props) => (props.isCurrent ? "#3b82f6" : "#1A1E24")};
  text-align: left;
`;

const RewardValueCurrent = styled.div<{ isCurrent: boolean }>`
  font-size: 16px;
  font-weight: normal;
  color: ${(props) => (props.isCurrent ? "#3b82f6" : "#1A1E24")};
  text-align: left;
  white-space: nowrap;
  margin-left: -45px;
`;

const NoRewardText = styled.div<{ isCurrent: boolean }>`
  font-size: 16px;
  font-weight: normal;
  color: ${(props) => (props.isCurrent ? "#3b82f6" : "#1A1E24")};
  margin-left: -45px;
`;

const NoRewardRankValue = styled.div<{ isCurrent: boolean }>`
  font-size: 16px;
  font-weight: 400;
  color: ${(props) => (props.isCurrent ? "#3b82f6" : "#1A1E24")};
`;

const NoRewardPromotersColumn = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: flex-start;
  margin-left: -40px;
`;

const NoRewardResultsColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  min-height: 70px;
  justify-content: center;
  margin-left: 50px;
`;

const NoRewardResultsText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #9ca3af;
  font-style: italic;
`;

const CurrentPromoterChip = styled.div`
  padding: 10px 18px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
  border: 1px solid #518ef8;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  min-width: 200px;
  background: #f0f9ff;
  color: #518ef8;

  .bold {
    font-weight: 700;
  }
`;

// Mobile components for rank rewards
const MobileRankCards = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`;

const MobileRankCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #f1f5f9;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const MobileCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const MobileRankValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #6e7882;
`;

const MobileRewardName = styled.div<{ isCurrent?: boolean }>`
  font-size: 18px;
  font-weight: 700;
  color: ${(props) => (props.isCurrent ? "#3b82f6" : "#1A1E24")};
  line-height: 1.3;
`;

const MobilePromotersSection = styled.div`
  margin-bottom: 20px;
`;

const MobilePromotersLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #6e7882;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

const MobilePromotersGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: flex-start;
`;

const MobileResultsArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
`;

const MobileResultsLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #6e7882;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const ArrowChipContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
`;

const ActionChip = styled.div<{ variant: "gain" | "lose" | "none" }>`
  padding: 12px 20px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  min-width: 200px;

  ${(props) => {
    if (props.variant === "gain") {
      return `
        background: #d1fae5;
        color: #12B981;
        border: 1px solid #12B981;
      `;
    } else if (props.variant === "lose") {
      return `
        background: #fce7f3;
        color: #DE5F85;
        border: 1px solid #DE5F85;
      `;
    } else {
      return `
        background: #f3f4f6;
        color: #808A97;
        border: 1px solid #808A97;
      `;
    }
  }}

  .bold {
    font-weight: 700;
  }
`;

interface PromoterDashboardRewardsByRankProps {
  promoterId: string;
  fundraiserId: string;
  ranks: RankAchievementModel[];
  promoter: PromoterProgressModel;
  promoters?: PromoterProgressModel[];
}

export const PromoterDashboardRewardsByRank: React.FC<PromoterDashboardRewardsByRankProps> =
  observer(({ promoterId, fundraiserId, ranks, promoter, promoters }) => {
    const [promoterImageErrors, setPromoterImageErrors] = useState<Set<string>>(
      new Set()
    );

    const getInitials = (name: string) => {
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase();
    };

    const handlePromoterImageError = (promoterId: string) => {
      setPromoterImageErrors((prev) => {
        const next = new Set(prev);
        next.add(promoterId);
        return next;
      });
    };

    const renderPromoterAvatar = (
      promoter: PromoterWithRank,
      isCurrentPromoter: boolean
    ) => {
      if (!promoter.name) return null;
      if (promoterImageErrors.has(promoter.id)) {
        return (
          <PromoterCircleInitials>
            {getInitials(promoter.name || "")}
          </PromoterCircleInitials>
        );
      }

      return (
        <PromoterCircleImage
          src={promoter.name}
          alt={promoter.name}
          onError={() => handlePromoterImageError(promoter.id)}
          style={{
            filter: isCurrentPromoter ? "none" : "grayscale(100%)",
          }}
        />
      );
    };

    // Rank-based reward logic
    const allPromoters = promoters || [];
    const sortedPromoters = allPromoters
      .slice()
      .sort((a, b) => b.totalRaisedAmount - a.totalRaisedAmount)
      .map(
        (p, index) =>
          new PromoterWithRank({
            ...p,
            currentRank: p.rank || index + 1,
            actualRank: p.rank || index + 1,
            name: p.firstName + " " + p.lastName,
            totalRaised: p.totalRaisedAmount,
          })
      ); // Ensure actualRank is set

    const currentPromoterRank =
      sortedPromoters.find((p) => p.id === promoterId)?.currentRank || 1;

    // Get rank reward tiers
    const getRankRewardTiers = () => {
      const tiers = [...ranks];

      // Check if any promoters are assigned to "No Reward" using the new logic
      const assignments = assignPromotersToRankTiers(sortedPromoters, ranks);
      const promotersWithoutRewards = assignments.filter(
        (a) => a.rankLevel === null
      );

      if (promotersWithoutRewards.length > 0) {
        // Calculate what the highest rank would be for "No Reward"
        const highestDefinedRank = Math.max(
          ...ranks.map((rank) => {
            const rankRange = rank.rank.toString();
            const rangeMatch = rankRange.match(/^(\d+)-(\d+)$/);
            const singleMatch = rankRange.match(/^(\d+)$/);

            if (singleMatch) {
              return parseInt(singleMatch[1]);
            } else if (rangeMatch) {
              return parseInt(rangeMatch[2]);
            }
            return 1;
          })
        );

        tiers.push({
          id: "no-reward",
          rank: `${highestDefinedRank + 1}+`,
          reward: "No Reward",
          order: 999,
        });
      }

      return tiers.sort((a, b) => {
        const aRank =
          typeof a.rank === "string" ? parseInt(a.rank.split("-")[0]) : a.rank;
        const bRank =
          typeof b.rank === "string" ? parseInt(b.rank.split("-")[0]) : b.rank;
        return (aRank || 0) - (bRank || 0);
      });
    };

    const rankTiers = getRankRewardTiers();
    // Calculate what the promoter needs to gain/lose for each tier
    const calculateRankActions = (tier: RankAchievementModel) => {
      // Ensure promoter.totalRaised is always a number to prevent NaN calculations
      const promoterTotalRaised = Number(promoter.totalRaisedAmount) || 0;

      // Get assignments using the new cascading logic
      const assignments = assignPromotersToRankTiers(sortedPromoters, ranks);

      const tierRankRange = tier.rank.toString();
      const rangeMatch = tierRankRange.match(/^(\d+)-(\d+)$/);
      const singleMatch = tierRankRange.match(/^(\d+)$/);
      const isNoRewardTier = tier.id === "no-reward";

      let tierStartRank = 1;
      let tierEndRank = 1;

      if (singleMatch) {
        tierStartRank = tierEndRank = parseInt(singleMatch[1]);
      } else if (rangeMatch) {
        tierStartRank = parseInt(rangeMatch[1]);
        tierEndRank = parseInt(rangeMatch[2]);
      } else if (isNoRewardTier) {
        const highestDefinedRank = Math.max(
          ...ranks.map((r) => {
            const rRange = r.rank.toString();
            const rRangeMatch = rRange.match(/^(\d+)-(\d+)$/);
            const rSingleMatch = rRange.match(/^(\d+)$/);
            if (rSingleMatch) return parseInt(rSingleMatch[1]);
            if (rRangeMatch) return parseInt(rRangeMatch[2]);
            return 1;
          })
        );
        tierStartRank = highestDefinedRank + 1;
        tierEndRank = 999;
      }

      // Use the new cascading assignment to determine current promoter's tier
      const currentAssignment = assignments.find(
        (a) => a.promoter.id === promoter.id
      );
      const isCurrentTier = isNoRewardTier
        ? currentAssignment?.rankLevel === null
        : currentAssignment?.rankLevel?.id === tier.id;

      if (isCurrentTier) {
        // Your Level: Show "you are here" with current icon
        return {
          isCurrentTier: true,
          actions: [
            {
              type: "current" as const,
              text: "You are here",
            },
          ],
        };
      }

      // Get promoters currently in this tier using assignments
      const currentTierPromoters = isNoRewardTier
        ? assignments.filter((a) => a.rankLevel === null).map((a) => a.promoter)
        : assignments
            .filter((a) => a.rankLevel?.id === tier.id)
            .map((a) => a.promoter);

      // Determine if this tier is above or below the current promoter
      const currentPromoterAssignment = assignments.find(
        (a) => a.promoter.id === promoter.id
      );
      const currentPromoterTierRank = currentPromoterAssignment?.rankLevel
        ? parseInt(
            currentPromoterAssignment.rankLevel.rank.toString().split("-")[0]
          )
        : 999; // If no tier, treat as very low rank

      const isTierAbovePromoter = tierStartRank < currentPromoterTierRank; // Better rank (lower number)
      const isTierBelowPromoter = tierEndRank > currentPromoterTierRank; // Worse rank (higher number)

      const actions: Array<{
        type: "gain" | "lose" | "current" | "none";
        amount?: number;
        text: string;
      }> = [];

      if (isTierAbovePromoter) {
        // Levels Above You
        if (isTierAbovePromoter) {
          // Find how much this tier *requires* (not based on other promoters)
          const tierMinimumRequired = tier.minimum || 0;

          // Calculate how much the current promoter has already raised
          const promoterTotal = Number(promoter.totalRaisedAmount) || 0;

          // Determine amount still needed to reach this tier
          const amountNeeded = Math.max(tierMinimumRequired - promoterTotal, 0);

          // Only show if they haven’t already surpassed this level
          if (amountNeeded > 0) {
            actions.push({
              type: "gain",
              amount: amountNeeded,
              text: `You need $${amountNeeded.toLocaleString()} more`,
            });
          } else {
            actions.push({
              type: "none",
              text: "Already reached this level",
            });
          }
        } else {
          // If there are 0 promoters on that higher level due to minimum donations not being met
          const minimumRequired = tier.minimum || 1;
          const amountNeeded = minimumRequired - promoterTotalRaised;
          actions.push({
            type: "gain",
            amount: amountNeeded,
            text: `You need $${amountNeeded.toLocaleString()} more`,
          });
        }
      } else if (isTierBelowPromoter) {
        // Levels Below You
        if (currentTierPromoters.length > 0) {
          // If there are > 0 promoters on a lower level
          const topInTier = currentTierPromoters
            .slice()
            .sort((a, b) => b.totalRaised - a.totalRaised)[0];
          const amountAhead = promoterTotalRaised - topInTier.totalRaised;

          actions.push({
            type: "lose",
            amount: amountAhead,
            text: `You are $${amountAhead.toLocaleString()} ahead`,
          });
        } else {
          // If there are 0 promoters on a lower level
          actions.push({
            type: "none",
            text: "No one at this level",
          });
        }
      }

      return { isCurrentTier: false, actions };
    };

    // Get assignments using the new cascading logic
    const assignments = assignPromotersToRankTiers(sortedPromoters, ranks);

    // Check if we should hide "No Reward" row
    const shouldHideNoRewardRow = () => {
      // Find current promoter's assignment
      const currentAssignment = assignments.find(
        (a) => a.promoter.id === promoter.id
      );
      const isCurrentPromoterInNoReward = currentAssignment?.rankLevel === null;

      // If current promoter is in a rank (not in No Reward)
      if (!isCurrentPromoterInNoReward) {
        // Check if No Reward tier has any promoters
        const noRewardPromoters = assignments
          .filter((a) => a.rankLevel === null)
          .map((a) => a.promoter)
          .filter((p) => p.totalRaised > 0); // Only count promoters with donations > 0

        return noRewardPromoters.length === 0;
      }

      return false; // Don't hide if current promoter is in No Reward
    };

    return (
      <>
        {/* Desktop Table */}
        <RankTable>
          <RankTableHeader>
            <div style={{ textAlign: "left" }}>RANK</div>
            <div style={{ marginLeft: "-45px" }}>REWARD</div>
            <div style={{ textAlign: "left", marginLeft: "-40px" }}>
              PROMOTERS
            </div>
            <div style={{ textAlign: "left", marginLeft: "50px" }}>
              DONATIONS
            </div>
          </RankTableHeader>

          {rankTiers
            .filter((tier: RankAchievementModel) => {
              // Hide "No Reward" row if conditions are met
              if (tier.id === "no-reward") {
                return !shouldHideNoRewardRow();
              }
              return true;
            })
            .map((tier: RankAchievementModel, index) => {
              const { isCurrentTier, actions } = calculateRankActions(tier);

              // Get promoters for this tier
              const getPromotersForTier = () => {
                if (tier.id === "no-reward") {
                  // Use the new cascading assignments
                  const assignments = assignPromotersToRankTiers(
                    sortedPromoters,
                    ranks
                  );
                  const noRewardPromoters = assignments
                    .filter((a) => a.rankLevel === null)
                    .map((a) => a.promoter);
                  // For "No Reward" tier, only show promoters with donations > 0, except always show current promoter
                  return noRewardPromoters.filter(
                    (p) => p.totalRaised > 0 || p.id === promoter.id
                  );
                }
                // Use the new cascading assignments for regular tiers
                const assignments = assignPromotersToRankTiers(
                  sortedPromoters,
                  ranks
                );
                return assignments
                  .filter((a) => a.rankLevel?.id === tier.id)
                  .map((a) => a.promoter);
              };

              const tierPromoters = getPromotersForTier();
              const hasOverflow = tierPromoters.length > 5;
              const displayPromoters = hasOverflow
                ? tierPromoters.slice(0, 5)
                : tierPromoters;
              const overflowCount = hasOverflow ? tierPromoters.length - 5 : 0;

              // Special handling for No Reward tier
              if (tier.id === "no-reward") {
                const isCurrentInNoReward = tierPromoters.some(
                  (p) => p.id === promoter.id
                );

                return (
                  <NoRewardRow key={tier.id}>
                    <NoRewardRankValue isCurrent={isCurrentInNoReward}>
                      {tier.rank}
                    </NoRewardRankValue>
                    <NoRewardText isCurrent={isCurrentInNoReward}>
                      {tier.reward}
                    </NoRewardText>
                    <NoRewardPromotersColumn>
                      {displayPromoters.length > 0 && (
                        <>
                          {displayPromoters.map((tierPromoter) => {
                            const isCurrentInTier =
                              tierPromoter.id === promoter.id;
                            return (
                              <PromoterCircle
                                key={tierPromoter.id}
                                isCurrentPromoter={isCurrentInTier}
                              >
                                {renderPromoterAvatar(
                                  tierPromoter,
                                  isCurrentInTier
                                )}
                              </PromoterCircle>
                            );
                          })}
                          {hasOverflow && (
                            <PromoterCountBadge>
                              +{overflowCount}
                            </PromoterCountBadge>
                          )}
                        </>
                      )}
                    </NoRewardPromotersColumn>
                    <NoRewardResultsColumn>
                      {isCurrentInNoReward ? (
                        <CurrentPromoterText>
                          {/* <img
                            src="/current.svg"
                            alt="Current"
                            width={16}
                            height={16}
                          /> */}
                          <Circle className="text-blue-500" />
                          <span>You are here</span>
                        </CurrentPromoterText>
                      ) : (
                        <ResultsText
                          variant="none"
                          style={{ color: "#808A97" }}
                        >
                          {/* <img
                            src="/none.svg"
                            alt="None"
                            width={16}
                            height={16}
                          /> */}
                          <XCircle className="text-gray-400" />
                          <span style={{ marginLeft: "4px" }}>
                            No one at this level
                          </span>
                        </ResultsText>
                      )}
                    </NoRewardResultsColumn>
                  </NoRewardRow>
                );
              }

              return (
                <RankTableRow key={tier.id}>
                  <RankValueCurrent isCurrent={isCurrentTier}>
                    {tier.rank}
                  </RankValueCurrent>
                  <RewardValueCurrent isCurrent={isCurrentTier}>
                    {tier.reward}
                  </RewardValueCurrent>
                  <PromotersColumn>
                    {displayPromoters.length > 0 ? (
                      <>
                        {displayPromoters.map((tierPromoter) => {
                          const isCurrentInTier =
                            tierPromoter.id === promoter.id;
                          return (
                            <PromoterCircle
                              key={tierPromoter.id}
                              isCurrentPromoter={isCurrentInTier}
                            >
                              {renderPromoterAvatar(
                                tierPromoter,
                                isCurrentInTier
                              )}
                            </PromoterCircle>
                          );
                        })}
                        {hasOverflow && (
                          <PromoterCountBadge>
                            +{overflowCount}
                          </PromoterCountBadge>
                        )}
                      </>
                    ) : null}
                  </PromotersColumn>
                  <ResultsColumn>
                    {actions.map((action, actionIndex) => (
                      <React.Fragment key={actionIndex}>
                        {action.type === "current" ? (
                          <CurrentPromoterText>
                            <img
                              src={Current}
                              alt="Current"
                              width={16}
                              height={16}
                            />
                            <span>{action.text}</span>
                          </CurrentPromoterText>
                        ) : action.type === "gain" ? (
                          <ResultsText variant={action.type}>
                            <img src={Up} alt="Up" width={16} height={16} />
                            <span style={{ marginLeft: "4px" }}>
                              {action.text}
                            </span>
                          </ResultsText>
                        ) : action.type === "lose" ? (
                          <ResultsText variant={action.type}>
                            <img src={Down} alt="Down" width={16} height={16} />
                            <span style={{ marginLeft: "4px" }}>
                              {action.text}
                            </span>
                          </ResultsText>
                        ) : action.type === "none" ? (
                          <ResultsText
                            variant="lose"
                            style={{ color: "#808A97" }}
                          >
                            <img src={None} alt="None" width={16} height={16} />
                            <span style={{ marginLeft: "4px" }}>
                              {action.text}
                            </span>
                          </ResultsText>
                        ) : null}
                      </React.Fragment>
                    ))}
                  </ResultsColumn>
                </RankTableRow>
              );
            })}
        </RankTable>

        {/* Mobile Cards */}
        <MobileRankCards>
          {rankTiers
            .filter((tier) => {
              // Hide "No Reward" row if conditions are met
              if (tier.id === "no-reward") {
                return !shouldHideNoRewardRow();
              }
              return true;
            })
            .map((tier, index) => {
              const { isCurrentTier, actions } = calculateRankActions(tier);

              // Get promoters for this tier
              const getPromotersForTier = () => {
                if (tier.id === "no-reward") {
                  // For "No Reward" tier, get promoters who don't qualify for any rank
                  return sortedPromoters.filter((promoter) => {
                    const { rankLevel } = calculatePromoterRankLevel(
                      promoter,
                      sortedPromoters,
                      ranks
                    );
                    return rankLevel === null;
                  });
                }

                return getPromotersForRankLevel(
                  tier,
                  sortedPromoters,
                  rankTiers
                );
              };

              const tierPromoters = getPromotersForTier();
              const hasOverflow = tierPromoters.length > 5;
              const displayPromoters = hasOverflow
                ? tierPromoters.slice(0, 5)
                : tierPromoters;
              const overflowCount = hasOverflow ? tierPromoters.length - 5 : 0;

              return (
                <MobileRankCard key={tier.id}>
                  <MobileCardHeader>
                    <MobileRewardName isCurrent={isCurrentTier}>
                      {tier.reward}
                    </MobileRewardName>
                    <MobileRankValue>Rank {tier.rank}</MobileRankValue>
                  </MobileCardHeader>

                  <MobilePromotersSection>
                    <MobilePromotersLabel>PROMOTERS</MobilePromotersLabel>
                    <MobilePromotersGrid>
                      {tierPromoters.length > 0 ? (
                        <>
                          {displayPromoters.map((tierPromoter) => {
                            const isCurrentInTier =
                              tierPromoter.id === promoter.id;
                            return (
                              <PromoterCircle
                                key={tierPromoter.id}
                                isCurrentPromoter={isCurrentInTier}
                              >
                                {renderPromoterAvatar(
                                  tierPromoter,
                                  isCurrentInTier
                                )}
                              </PromoterCircle>
                            );
                          })}
                          {hasOverflow && (
                            <PromoterCountBadge>
                              +{overflowCount}
                            </PromoterCountBadge>
                          )}
                        </>
                      ) : null}
                    </MobilePromotersGrid>
                  </MobilePromotersSection>

                  <MobileResultsArea>
                    <MobileResultsLabel>DONATIONS</MobileResultsLabel>
                    {actions.map((action, actionIndex) => (
                      <ArrowChipContainer key={actionIndex}>
                        {action.type === "current" ? (
                          <CurrentPromoterChip>
                            <Circle className="text-blue-500" />
                            <span className="bold">{action.text}</span>
                          </CurrentPromoterChip>
                        ) : action.type === "gain" ? (
                          <ActionChip variant={action.type}>
                            <ArrowUpRight className="text-green-500" />
                            <span style={{ marginLeft: "4px" }}>
                              {action.text}
                            </span>
                          </ActionChip>
                        ) : action.type === "lose" ? (
                          <ActionChip variant={action.type}>
                            <ArrowDownRight className="text-red-500" />
                            <span style={{ marginLeft: "4px" }}>
                              {action.text}
                            </span>
                          </ActionChip>
                        ) : action.type === "none" ? (
                          <ActionChip variant={action.type}>
                            <XCircle className="text-gray-400" />
                            <span style={{ marginLeft: "4px" }}>
                              {action.text}
                            </span>
                          </ActionChip>
                        ) : null}
                      </ArrowChipContainer>
                    ))}
                  </MobileResultsArea>
                </MobileRankCard>
              );
            })}
        </MobileRankCards>
      </>
    );
  });
